import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, getLandingPath } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Mail, Lock, Eye, EyeOff, IdCard } from 'lucide-react';

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 mr-2.5">
    <path fill="#EA4335" d="M12 5c1.6 0 3 .6 4.1 1.6l3.1-3.1C17 1.4 14.7.4 12 .4 7.3.4 3.3 3 1.4 7l3.6 2.8C5.9 7 8.7 5 12 5z" />
    <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5c-.3 1.5-1.2 2.7-2.4 3.6l3.7 2.9c2.2-2 3.5-5 3.5-8.7z" />
    <path fill="#FBBC05" d="M5 14.2c-.3-.7-.4-1.4-.4-2.2s.1-1.5.4-2.2L1.4 7C.5 8.5 0 10.2 0 12s.5 3.5 1.4 5l3.6-2.8z" />
    <path fill="#34A853" d="M12 23.6c3.2 0 6-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.1-4.2 1.1-3.3 0-6.1-2-7-4.8L1.4 17C3.3 21 7.3 23.6 12 23.6z" />
  </svg>
);

/**
 * Login methods component.
 *  - Primary (default `mode === 'employee'`): User ID + Password (for all hospital staff).
 *  - Secondary (`mode === 'admin'`): Email + Password for Super Admin. Google sign-in stays here for the
 *    Super Admin user (klickapps2026@gmail.com) as a convenience.
 *  - After login, if `must_change_password` is set on the user, force redirect to /change-password.
 */
export const LoginMethods = ({ theme = 'default', primaryColor = '#0d9488', secondaryColor = '#14b8a6' }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('employee'); // 'employee' | 'admin'
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const result = await login(identifier.trim(), password);
      if (!result.success) {
        setError(result.error || 'Login failed');
        return;
      }
      // Force-reset flow
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.must_change_password) {
        navigate('/change-password', { replace: true, state: { firstLogin: true } });
      } else {
        navigate(getLandingPath(user), { replace: true });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const inputStyle = theme === 'branded' ? 'bg-slate-50/50 focus:bg-white' : '';
  const btnStyle = theme === 'branded'
    ? { background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, boxShadow: `0 8px 24px ${secondaryColor}33` }
    : {};
  const btnClass = theme === 'branded'
    ? 'w-full h-12 rounded-xl text-white font-semibold text-base shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-[1.01]'
    : 'w-full h-12 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold';

  const isEmployee = mode === 'employee';
  const idLabel = isEmployee ? 'Employee ID' : 'Email Address';
  const idPlaceholder = isEmployee ? 'e.g., PMHS0000001' : 'admin@klickapps.com';
  const IdIcon = isEmployee ? IdCard : Mail;

  return (
    <div className="space-y-4" data-testid="login-methods">
      {error && (
        <Alert variant="destructive" className="rounded-xl">
          <AlertDescription data-testid="login-error">{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label className="text-sm font-medium">{idLabel}</Label>
          <div className="relative">
            <IdIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type={isEmployee ? 'text' : 'email'}
              placeholder={idPlaceholder}
              value={identifier}
              onChange={(e) => setIdentifier(isEmployee ? e.target.value.toUpperCase() : e.target.value)}
              className={`pl-11 h-12 rounded-xl tracking-wide ${inputStyle}`}
              required
              autoComplete={isEmployee ? 'username' : 'email'}
              data-testid={isEmployee ? 'employee-id-input' : 'email-input'}
              autoFocus
            />
          </div>
          {isEmployee && (
            <p className="text-[11px] text-slate-500 pl-1">
              Your hospital&apos;s Employee ID. Format: <span className="font-mono">XXXX0000000</span> — provided by your HR team.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium">Password</Label>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`pl-11 pr-11 h-12 rounded-xl ${inputStyle}`}
              required
              autoComplete="current-password"
              data-testid="password-input"
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {isEmployee && (
            <p className="text-[11px] text-slate-500 pl-1">
              First-time login? Use the temporary password from your Welcome Email — you&apos;ll be asked to set a new one.
            </p>
          )}
        </div>

        <Button type="submit" className={btnClass} style={btnStyle} disabled={isLoading} data-testid="login-submit-btn">
          {isLoading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Signing in...</> : 'Sign In'}
        </Button>
      </form>

      <div className="pt-1 text-center">
        {isEmployee ? (
          <button type="button" onClick={() => { setMode('admin'); setIdentifier(''); setPassword(''); setError(''); }}
            className="text-xs text-slate-400 hover:text-slate-600 hover:underline transition" data-testid="admin-mode-btn">
            Platform Admin · Email login
          </button>
        ) : (
          <div className="space-y-3">
            <Button onClick={handleGoogle} type="button"
              className="w-full h-11 rounded-xl bg-white hover:bg-slate-50 text-slate-800 font-medium border border-slate-200 shadow-sm"
              data-testid="google-signin-btn">
              <GoogleIcon /> Sign in with Google (Super Admin)
            </Button>
            <button type="button" onClick={() => { setMode('employee'); setIdentifier(''); setPassword(''); setError(''); }}
              className="text-xs text-slate-400 hover:text-slate-600 hover:underline transition" data-testid="back-to-employee-btn">
              ← Back to Employee ID login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
